import { ajax } from '../../utils/ajax-handler';
import { get, clone } from '../../utils/object-helpers';
import storeEvent from '../../utils/store-event';
import ModelHelper from './model-helper';
import menusBuilder from './builders/menus';
import quantityBuilder from './builders/quantity';
import priceBuilder from './builders/price';
import confidenceBuilder from './builders/confidence';
import cartButtonBuilder from './builders/cart-button';

const CLS = {
    active: 'active',
    shrink: 'shrink',
    fadeOut: 'fade-out',
    show: 'show',
    hide: 'hide',
    carouselList: 'str-quickview-carousel__list'
};
const SEL = {
    thumb: '.str-quickview__thumb',
    activeThumb: `.str-quickview__thumb.${CLS.active}`,
    heroImage: '.str-quickview__hero-image',
    spinner: '.str-quickview__spinner',
    thumbSpinner: '.str-quickview__thumb__spinner',
    carouselWrap: '.str-quickview__thumb-carousel-wrap',
    carouselItem: '.str-quickview-carousel__list li',
    waitingSpacer: '.str-quickview__thumb-carousel-spacer.waiting',
    firstMenuWithError: '.str-quickview__menu-group span[aria-invalid=true] button'
};
const ATTR = {
    dataImageId: 'data-image-id'
};
const ERROR_TYPE_OUT_OF_STOCK = 'OUT_OF_STOCK';
const MAX_THUMBS_IN_VIEWPORT = 5;
const SERVICE_TIMEOUT = 8000;
const IMAGE_TIMEOUT = 4000; // Needs to be less than SERVICE_TIMEOUT
const AJAX_TIMEOUT = 8000;

export default class {
    init() {
        this.state = {
            carousel: null,
            cartButton: null,
            closeDialogText: null,
            condition: null,
            confidence: null,
            customization: null,
            eek: null,
            eekTire: null,
            errorMessage: null,
            hasAuction: false,
            hasVariations: false,
            heroImage: null,
            isWatching: false,
            itemId: null,
            mediaList: null,
            menus: null,
            open: false,
            price: null,
            quantity: null,
            quickviewWatch: null,
            shippingReturns: null,
            title: null,
            titleTag: null,
            variationId: -1,
            viewItemButton: null
        };
        this.helper = null;
        this.hasContent = false;
        this.imageTimeoutId = null;
        this.isHeroImageLoaded = false;
        this.itemCard = null;
        this.itemMap = {};
        this.resp = null;
        this.serviceTimeoutId = null;
    }
    onCreate(input) {
        this.init();
        this.defaultErrorMessage = get(input, 'page.info.content.defaultErrorMessage', '');
        this.storeName = this.getStoreName(input);
        this.ajaxPath = get(input, 'config.ajaxPath', '');
    }
    onInput(input) {
        return {
            page: get(input, 'page', null),
            spinnerText: get(input, 'page.modules.LOADING_SPINNER_MODULE.accessibilityText', '')
        };
    }
    onMount() {
        // The quickview-button sends itemCard data.
        storeEvent.openQuickview.listen(async itemCard => {
            this.openDialog();
            this.itemCard = itemCard;
            this.state.itemId = this.itemCard.listingId;
            try {
                this.itemMap[this.state.itemId] = await this.fetchQuickview(this.state.itemId);
                this.updateQuickviewOnMount();
            } catch (err) {
                this.state.errorMessage = this.defaultErrorMessage;
            }
        });
        storeEvent.watchAttempt.listen(itemId => {
            if (itemId === this.state.itemId) {
                this.checkForErrors();
            }
        });
        storeEvent.watchSuccess.listen(data => {
            if (data.itemId === this.state.itemId && this.state.quickviewWatch) {
                const clonedWatch = clone(this.state.quickviewWatch);
                clonedWatch.isWatching = data.isWatching;
                this.state.quickviewWatch = clonedWatch;
            }
        });
    }
    updateQuickviewOnMount() {
        this.resp = this.itemMap[this.state.itemId];
        if (this.resp) {
            this.helper = new ModelHelper(this.resp, this.itemCard);
            this.hasContent = !!this.helper.getTitle().text;
            if (this.hasContent) {
                // State
                this.state.carousel = this.resp.carousel;
                this.state.cartButton = cartButtonBuilder(this.resp);
                this.state.closeDialogText = this.resp.closeDialogText;
                this.state.condition = this.resp.condition;
                this.state.confidence = confidenceBuilder(this.resp);
                this.state.customization = this.resp.customization;
                this.state.eek = this.helper.getEEK();
                this.state.eekTire = this.helper.getEEKTire();
                this.state.hasAuction = this.resp.hasAuction;
                this.state.hasVariations = this.resp.hasVariations;
                this.state.heroImage = this.helper.getHeroImage();
                this.state.isWatching = this.isWatching();
                this.state.mediaList = this.resp.mediaList;
                this.state.menus = menusBuilder(this.resp);
                this.state.price = priceBuilder(this.resp, null, this.itemCard);
                this.state.quantity = quantityBuilder(this.resp);
                this.state.quickviewWatch = get(this.resp, 'watch', null);
                this.state.shippingReturns = this.helper.getShippingReturns();
                this.state.title = this.helper.getTitle();
                this.state.titleTag = this.helper.getTitleTag();
                this.state.variationId = -1;
                this.state.viewItemButton = this.helper.getViewItemButton();
                this.update();

                // Sometimes items have pre-selected aspects.
                // Need to filter out items that are invalid for the selected combo,
                // set the variation id, set hero image, etc.
                const selectedValueIds = this.getSelectedValueIds();
                if (selectedValueIds.length) {
                    this.updateAllOnMount();
                }
            } else {
                this.showDefaultError();
            }
        }
    }
    openDialog() {
        this.state.open = true;
        this.state.errorMessage = null;

        // If image doesn't load, check if service sent content.
        this.imageTimeoutId = setTimeout(() => {
            if (this.hasContent) {
                this.fadeOutSpinner();
            }
        }, IMAGE_TIMEOUT);

        // If service doesn't load, then show error message.
        this.serviceTimeoutId = setTimeout(() => {
            this.showDefaultError();
        }, SERVICE_TIMEOUT);
    }
    closeDialog() {
        clearTimeout(this.imageTimeoutId);
        clearTimeout(this.serviceTimeoutId);
        this.init();
    }
    fetchQuickview(itemId) {
        return new Promise((resolve, reject) => {
            const ajaxUrl = buildQuickviewUrl(this.ajaxPath, itemId, this.storeName);
            ajax({ url: ajaxUrl }, {
                successCallBack: response => {
                    resolve(response);
                },
                errorCallBack: error => {
                    reject(error);
                }
            }, AJAX_TIMEOUT);
        });
    }
    onSelectAspect(menuId, evt) {
        // If '- Select -' is selected value is 'undefined' string
        const selectedValueIdString = get(evt, 'selected.0', null);
        let selectedValueId = parseInt(selectedValueIdString);
        selectedValueId = isNaN(selectedValueId) ? -1 : selectedValueId; // Zero is a valid selectedValueId
        if (menuId) {
            this.updateAllForSelection(menuId, selectedValueId);
        }
    }
    onSelectQuantity(evt) {
        const quantity = quantityBuilder(this.resp);
        quantity.selected = parseInt(
            get(
                evt,
                'value',
                get(evt, 'selected.0', 1))
        );
        quantity.max = this.state.quantity.max;

        if (quantity.selected >= 1 && quantity.selected <= quantity.max) {
            quantity.error = null;
        } else {
            const variation = this.helper.getVariation(this.state.variationId);
            const validations = get(variation, 'quantity.quantityInput.validations.0', null);
            const message = get(validations, 'maxErrorMessage.title.textSpans.0.text', null);
            quantity.error = {
                message,
                type: 'QUANTITY'
            };
        }

        this.state.quantity = quantity;
    }
    // Wait for 2 things before fading out spinner:
    // 1) content
    // 2) the hero image
    onHeroImageLoad() {
        this.isHeroImageLoaded = true;
        if (this.hasContent) {
            this.fadeOutSpinner();
        }
    }
    onThumbLoad(evt) {
        const button = evt.target.closest('button');
        if (button) {
            const thumb = button.querySelector('img');
            const spinner = button.querySelector(SEL.thumbSpinner);
            thumb.style.opacity = 1;
            spinner.style.opacity = 0;
            setTimeout(() => {
                spinner.style.display = 'none';
            }, 250);
        }
    }
    onThumbMouseover(imageId) {
        if (imageId) {
            this.updateHeroImage(imageId);
        }
    }
    onThumbFocus(imageId) {
        if (imageId) {
            this.updateHeroImage(imageId);
        }
    }
    onThumbClick(imageId) {
        if (imageId) {
            this.setActiveThumb(imageId);
        }
    }
    // When mouse leaves carouselList,
    // update hero image to reflect the active thumb.
    // Only change when the mouse leaves the carouselList,
    // not the thumbs themselves. Otherwise, when the mouse
    // is over the gap between the thumbs, the hero image
    // flashes back to the active thumb, which looks bad.
    onCarouselMouseleave(evt) {
        if (evt.target.classList.contains(CLS.carouselList)) {
            this.updateHeroImage();
        }
    }
    // When focus leaves thumbs,
    // update hero image to reflect the active thumb.
    onCarouselBlur() {
        setTimeout(() => {
            const focusVisible = this.el.querySelector(`${SEL.thumb}:focus-visible`);
            const focus = this.el.querySelector(`${SEL.thumb}:focus`);
            if (!focusVisible && !focus) {
                this.updateHeroImage();
            }
        }, 0);
    }
    // menuId and selectedValueId are optional,
    // but if passed in, both are required.
    updateAllForSelection(menuId, selectedValueId) {
        this.updateMenus(menuId, selectedValueId);
        this.updateVariation();
        this.updateMaxQuantity();
        this.updateHeroImageForSelection(menuId, selectedValueId);
        this.scrollToActiveThumb(menuId);
        this.updateIsWatching();
    }
    updateAllOnMount() {
        this.updateAllForSelection();
    }
    // Set the value to the menu.
    // Then filter out items that are invalid for the selected combo.
    updateMenus(menuId, valueId) {
        const menus = clone(this.state.menus);
        if (typeof menuId !== 'undefined' && typeof valueId !== 'undefined') {
            this.setSelectedValueId(menus, menuId, valueId);
        }
        const selectedValueIds = this.getSelectedValueIds(menus);
        menus.forEach(menu => {
            // Items not valid for the selected combo will not be rendered in the menu.
            menu.items.forEach(item => {
                const check = this.helper.isValidForCombo(item, selectedValueIds);
                item.isValidForCombo = check.isValidForCombo;
                item.isComboOutOfStock = check.isComboOutOfStock;
            });
            // Clear errors
            if (menu.selectedValueId !== -1 ||
                (menu.error && menu.error.type === ERROR_TYPE_OUT_OF_STOCK)) {
                menu.error = null;
            }
        });
        this.state.menus = menus;
    }
    updateVariation() {
        const selectedValueIds = this.getSelectedValueIds();
        let variationId;
        let variation;

        if (selectedValueIds.length) {
            const key = selectedValueIds.join('_');
            const combos = this.resp.variationCombinations;
            variationId = combos[key];
        }

        if (variationId) {
            variation = this.helper.getVariation(variationId);
            this.state.variationId = variationId;
            this.state.price = priceBuilder(this.resp, variation);
        } else {
            // Reset to default variation and price
            this.state.variationId = -1;
            this.state.price = priceBuilder(this.resp);
        }

        // Update cartButton
        this.state.cartButton = cartButtonBuilder(this.resp, variation);
    }
    updateMaxQuantity() {
        const variation = this.helper.getVariation(this.state.variationId);
        const validations = get(variation, 'quantity.quantityInput.validations.0', null);
        const max = parseInt(get(validations, 'maxValue', -1));
        if (max !== -1) {
            const quantity = quantityBuilder(this.resp);
            quantity.max = max;
            quantity.selected = this.state.quantity.selected;
            if (quantity.selected > max) {
                quantity.error = {
                    message: get(validations, 'maxErrorMessage.title.textSpans.0.text', null),
                    type: 'QUANTITY'
                };
            } else {
                quantity.error = null;
            }
            this.state.quantity = quantity;
        }
    }
    updateHeroImageForSelection(_menuId, _selectedValueId) {
        let menuId = _menuId;
        let selectedValueId = _selectedValueId;
        let hasPictures;

        if (!menuId) {
            const menu = this.helper.getMenuWithPictures();
            if (menu) {
                menuId = menu.id;
                selectedValueId = menu.selectedValueId;
                hasPictures = true;
            }
        } else {
            hasPictures = this.helper.doesMenuHavePictures(menuId);
        }

        if (hasPictures) {
            let media;
            // Nothing selected (menu is reset). Go to first image.
            if (selectedValueId === -1 || selectedValueId === undefined) {
                media = this.helper.getMedia(0);
            } else {
                const imageMap = get(this.resp, 'menuItemPictureIndexMap', {});
                const imageIndexes = imageMap[selectedValueId.toString()];
                if (imageIndexes) {
                    const imgIndex = imageIndexes[0]; // Just take the fist one
                    media = this.helper.getMedia(imgIndex);
                }
            }

            // If image not found, don't do anything.
            // Some options are not mapped to images.
            const image = get(media, 'image.originalImg', null);
            if (image) {
                this.setHeroImage(image);
                const imageId = get(image, 'imageId', null);
                this.setActiveThumb(imageId);
            }
        }
    }
    updateHeroImage(imageId) {
        let imgId = imageId;

        if (!imgId) {
            const activeThumb = this.getActiveThumb();
            imgId = this.getImageId(activeThumb);
        }
        const image = this.helper.getImage(imgId);
        if (image) {
            this.setHeroImage(image);
        }
    }
    updateIsWatching() {
        this.state.isWatching = this.isWatching();
    }
    setHeroImage(image) {
        this.state.heroImage = {
            url: get(image, 'URL', ''),
            title: get(image, 'title', '')
        };
    }
    hideCarouselIfNoThumbs() {
        if (this.el && this.el.querySelector) {
            const carouselWrap = this.el.querySelector(SEL.carouselWrap);
            const waitingSpacer = this.el.querySelector(SEL.waitingSpacer);
            // If waitingSpacer is found, then it means there are no image thumbs.
            if (carouselWrap && waitingSpacer) {
                carouselWrap.classList.add(CLS.shrink);
            }
        }
    }
    showDefaultError() {
        this.fadeOutSpinner();
        this.state.errorMessage = this.defaultErrorMessage;
    }
    isWatching() {
        let isWatching = false;
        if (this.state.hasVariations) {
            const variation = this.helper.getVariation(this.state.variationId);
            if (variation) {
                const watchState = get(variation, 'watch.state', null);
                isWatching = watchState && watchState !== 'watch';
            } else {// Menu was unselected so there's no variation
                isWatching = false;
            }
        } else if (this.resp.watch) {
            isWatching = this.resp.watch.isWatching;
        }
        return isWatching;
    }
    fadeOutSpinner() {
        if (this.el) {
            clearTimeout(this.imageTimeoutId);
            clearTimeout(this.serviceTimeoutId);
            const spinner = this.el.querySelector(SEL.spinner);
            if (spinner && spinner.style.display !== 'none') {
                spinner.addEventListener('transitionend', () => {
                    spinner.style.display = 'none';
                });
                this.hideCarouselIfNoThumbs();
                this.setFirstThumbActiveIfNothingActive();
                spinner.classList.add(CLS.fadeOut);
            }
        }
    }
    setSelectedValueId(menus, menuId, itemValueId) {
        const menu = menus.find(menuItem => menuItem.id === menuId);
        if (menu) {
            menu.selectedValueId = itemValueId;
        }
    }
    // We want to scroll if there's no menuId, because that can
    // happen when the menu opens with pre-selected values.
    //
    // But if there is a menuId, we only want to scroll if the
    // menu is a "picture" menu (a menu mapped to images).
    scrollToActiveThumb(menuId) {
        const menuWithPics = this.helper.getMenuWithPictures();
        if (!menuId || (menuWithPics && menuId === menuWithPics.id)) {
            const thumbs = this.el.querySelectorAll(SEL.thumb);
            if (thumbs.length) {
                let activeThumbIndex = 0;
                for (let i = 0, n = thumbs.length; i < n; i++) {
                    if (thumbs[i].classList.contains(CLS.active)) {
                        activeThumbIndex = i;
                        break;
                    }
                }
                // maxIndex is to prevent scrolling to index that
                // would leave empty space in the carousel view.
                // The carouse view should always show 6 items if
                // there are at least 6 items total.
                const maxIndex = thumbs.length - MAX_THUMBS_IN_VIEWPORT;
                if (maxIndex > 0) {
                    if (activeThumbIndex > maxIndex) {
                        activeThumbIndex = maxIndex;
                    }
                } else {
                    activeThumbIndex = 0;
                }
                storeEvent.quickviewCarouselIndex.dispatch({ index: activeThumbIndex });
            }
        }
    }
    // An item can have pre-selected aspects. In this case,
    // the appropriate thumb may have already been set active.
    setFirstThumbActiveIfNothingActive() {
        const thumbs = this.el.querySelectorAll(SEL.thumb);
        if (thumbs.length) {
            for (let i = 0, n = thumbs.length; i < n; i++) {
                const isActive = thumbs[i].classList.contains(CLS.active);
                if (isActive) {
                    return;
                }
            }
            this.setActiveThumb(this.getImageId(thumbs[0]));
        }
    }
    setActiveThumb(imageId) {
        const thumbs = this.el.querySelectorAll(SEL.thumb);
        if (thumbs.length) {
            let targetIndex = 0;
            for (let i = 0, n = thumbs.length; i < n; i++) {
                const btn = thumbs[i];
                if (this.getImageId(btn) === imageId) {
                    targetIndex = i;
                }
                btn.blur();
                btn.classList.remove(CLS.active);
            }
            const targetThumb = thumbs[targetIndex];
            targetThumb.classList.add(CLS.active);
        }
    }
    getActiveThumb() {
        const thumbs = this.el.querySelectorAll(SEL.thumb);
        if (thumbs.length) {
            return Array.from(thumbs).find(el => el.classList.contains(CLS.active));
        }
    }
    getSelectedValueIds(_menus) {
        const menus = _menus || this.state.menus;
        const selectedMenus = menus.filter(menu => menu.selectedValueId !== -1);
        return selectedMenus.map(menu => menu.selectedValueId);
    }
    checkForErrors() {
        let errorMsgCount = 0;
        const clonedMenus = clone(this.state.menus);

        // Add error messages for unselected menus or
        // menus with a selection is that out-of-stock
        clonedMenus.forEach(menu => {
            if (menu.selectedValueId === -1) {
                menu.error = {
                    message: this.helper.getMenuErrorMsg(menu.id),
                    type: 'ASPECT'
                };
                errorMsgCount++;
            } else {
                const item = menu.items.find(menuItem => menuItem.valueId === menu.selectedValueId);
                if (item && item.outOfStock || item.isComboOutOfStock) {
                    menu.error = {
                        message: this.resp.outOfStockMessage,
                        type: ERROR_TYPE_OUT_OF_STOCK
                    };
                    errorMsgCount++;
                }
            }
        });
        this.state.menus = clonedMenus;

        if (this.state.quantity.errorMsg) {
            errorMsgCount++;
        }

        // Move focus to first menu with error
        if (errorMsgCount) {
            setTimeout(() => {
                const firstMenuWithError = this.el.querySelector(SEL.firstMenuWithError);
                if (firstMenuWithError) {
                    firstMenuWithError.focus();
                }
            }, 0);
        }

        return errorMsgCount;
    }
    onAddToCartClick() {
        const errorMsgCount = this.checkForErrors();

        if (!errorMsgCount) {
            const addToCartData = {
                itemId: this.state.itemId,
                variationId: this.state.variationId,
                quantity: this.state.quantity.selected,
                closeDialogText: this.state.closeDialogText
            };
            storeEvent.openAddToCart.dispatch(addToCartData);
            this.closeDialog();
        }
    }
    getStoreName(input) {
        return get(input, 'page.modules.PRESENCE_INFORMATION_MODULE.displayName.action.params.store_name', '');
    }
    getImageId(thumb) {
        if (thumb) {
            return thumb.getAttribute(ATTR.dataImageId);
        }
    }
}

export function buildQuickviewUrl(ajaxPath, itemId, storeName) {
    if (!ajaxPath || !itemId) {
        return;
    }
    const url = new URL(ajaxPath, window.location.origin);
    url.searchParams.append('item_id', itemId);
    if (storeName) {
        url.searchParams.append('store_name', storeName);
    }
    return url.href;
}
